/** How repeatable the driving was, metre by metre, across the best laps.
 *
 * The lap table's consistency figure is one number for the whole session.
 * This is the same question asked along the lap: the median speed of the
 * session's quickest counting laps, lined up on the fastest one's line, with
 * a band one deviation either side. Where the band is thin the driver does
 * the same thing every lap; where it swells is a corner taken a different
 * way each time, which is where a lap time is being left to chance.
 *
 * Five laps is the logger's default and the right number: fewer and a single
 * odd lap is half the band, more and the slow laps of a long stint widen it
 * everywhere. The documents are megabytes each and are fetched only when the
 * driver asks, which the button is for; a session with fewer than two
 * counting laps has nothing to say and says so.
 */

import type { ReactNode } from 'react';

import { Card, ErrorNote, Kicker, Spinner } from '@/components/ui';
import { count, lapTime } from '@/lib/format';
import { speedIn, speedLabel } from '@/lib/units';
import { useSpeedUnit } from '@/lib/useSpeedUnit';
import { CornerAxis, CornerLines } from '@/components/sessions/CornerAxis';
import {
  DEVIATION_BOX,
  cornerTicks,
  deviationDomain,
  deviationPaths,
  widestSpreads,
} from '@/components/sessions/compare';
import type { CornerMark, Deviation } from '@/components/sessions/compare';
import type { LapTiming } from '@/lib/types';

export interface ConsistencyCardProps {
  /** The laps the band is drawn over, quickest first. */
  laps: readonly LapTiming[];
  data: Deviation | null;
  corners: readonly CornerMark[];
  /** Whether the documents have been asked for. */
  wanted: boolean;
  loading: boolean;
  error: unknown;
  onLoad: () => void;
  onRetry: () => void;
}

const QUIET = 'var(--color-neutral-600)';

export function ConsistencyCard({
  laps,
  data,
  corners,
  wanted,
  loading,
  error,
  onLoad,
  onRetry,
}: ConsistencyCardProps) {
  const speedUnit = useSpeedUnit();
  const domain = deviationDomain(data);
  const paths = data && domain ? deviationPaths(data, domain) : null;
  const ticks = domain ? cornerTicks(corners, domain.maxDistance, DEVIATION_BOX) : [];
  const widest = widestSpreads(data);
  const megabytes = laps.reduce((sum, lap) => sum + lap.bytes, 0) / (1024 * 1024);

  return (
    <Card elevation="none" label="Consistency" style={{ padding: 12, gap: 6 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
        <Kicker>
          consistency · best {laps.length === 1 ? 'lap' : `${laps.length} laps`}
        </Kicker>
        <span style={{ fontSize: 11, color: 'var(--color-neutral-500)' }}>
          median speed, one deviation either side
        </span>
      </div>

      {laps.length < 2 ? (
        <Empty>Two counting laps are the least a spread can be drawn over.</Empty>
      ) : error ? (
        <ErrorNote compact error={error} title="could not load the laps" onRetry={onRetry} />
      ) : !wanted ? (
        <Empty>
          <button type="button" className="btn btn-secondary" style={{ fontSize: 12 }} onClick={onLoad}>
            Load the best {laps.length} laps ({count(Math.round(megabytes))} MB)
          </button>
        </Empty>
      ) : !domain || !paths ? (
        <Empty>{loading ? <Spinner label="lining the laps up" /> : 'The laps could not be compared.'}</Empty>
      ) : (
        <>
          <svg
            viewBox={`0 0 ${DEVIATION_BOX.width} ${DEVIATION_BOX.height}`}
            preserveAspectRatio="none"
            role="img"
            aria-label={`Median speed and its spread across the best ${laps.length} laps`}
            style={{ width: '100%', height: DEVIATION_BOX.height, display: 'block' }}
          >
            <CornerLines ticks={ticks} height={DEVIATION_BOX.height} />
            <path d={paths.band} fill="var(--color-accent)" fillOpacity={0.25} />
            <polyline
              points={paths.median}
              fill="none"
              stroke="var(--color-accent)"
              strokeWidth={1.2}
              vectorEffect="non-scaling-stroke"
            />
          </svg>
          <CornerAxis ticks={ticks} width={DEVIATION_BOX.width} />
          <p style={{ margin: 0, fontSize: 11.5, color: QUIET, textWrap: 'pretty' }}>
            Laps {laps.map((lap) => lap.lap).join(', ')} ({laps.map((lap) => lapTime(lap.lap_time_ms)).join(', ')}).{' '}
            {widest.length === 0
              ? 'The laps agree everywhere.'
              : `The laps disagree most ${widest
                  .map(
                    (run) =>
                      `between ${count(Math.round(run.from))} and ${count(Math.round(run.to))} m (±${count(
                        Math.round(speedIn(run.peak, speedUnit.unit)),
                      )} ${speedLabel(speedUnit.unit)})`,
                  )
                  .join(', ')}.`}
          </p>
        </>
      )}
    </Card>
  );
}

function Empty({ children }: { children: ReactNode }) {
  return (
    <div
      style={{
        display: 'grid',
        placeItems: 'center',
        minHeight: DEVIATION_BOX.height,
        fontSize: 12.5,
        color: QUIET,
        textAlign: 'center',
        textWrap: 'pretty',
      }}
    >
      {children}
    </div>
  );
}

export default ConsistencyCard;
